// Workforce Overview P2 — the presence table (Today and History share it) and
// the employee-day Attendance Detail drawer.
//
// ⚠️ Everything here DISPLAYS server decisions. Who may see which row, which
// rows a filter returns, and what a day's status is are all decided server-
// side; React never filters for security.
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Badge } from '@/features/shared/components/ui/badge';
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle,
} from '@/features/shared/components/ui/sheet';
import { getApiErrorMessage } from '@/lib/apiError';
import type { PresenceDay } from '@/services/workAssignmentService';
import { attendanceDayDetail, type AttendanceDayDetail } from '@/services/attendanceService';
import { KindBadge, type EmployeeMeta } from './shared';
import {
  attendanceCells, basisOf, dayStatusOf, exceptionCountOf, fmtHM, sourceChipOf, workedCell,
} from './presenceBasis';

const WorkedText: React.FC<{ minutes?: number; evidence?: Record<string, any> | null }> = ({ minutes, evidence }) => {
  const { t } = useTranslation('admin');
  const w = workedCell(minutes, evidence);
  const tone = w.tone === 'warning' ? 'text-warning' : w.tone === 'muted' ? 'text-muted-foreground' : 'text-foreground';
  const note = w.note ? t(w.note.key, { defaultValue: w.note.defaultValue }) : undefined;
  return (
    <span className={`tabular-nums ${tone}`} title={note}>
      {t(w.key, { defaultValue: w.defaultValue, ...(w.values || {}) })}{note ? ' *' : ''}
    </span>
  );
};

export const PresenceTable: React.FC<{
  rows: PresenceDay[];
  nameOf: (id?: string | null) => string;
  metaOf: (id?: string | null) => EmployeeMeta;
  onOpen: (d: PresenceDay) => void;
  showDate?: boolean;
}> = ({ rows, nameOf, metaOf, onOpen, showDate }) => {
  const { t } = useTranslation('admin');
  const th = 'py-2 px-3 font-medium whitespace-nowrap';
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-muted-foreground border-b border-border">
            {showDate && <th className={th}>{t('wa.col_date', { defaultValue: 'Date' })}</th>}
            <th className={th}>{t('wa.col_employee', { defaultValue: 'Employee' })}</th>
            <th className={th}>{t('wa.col_dept_branch', { defaultValue: 'Department / Branch' })}</th>
            <th className={th}>{t('wa.col_presence', { defaultValue: 'Presence' })}</th>
            <th className={th}>{t('wa.col_first_in', { defaultValue: 'First in' })}</th>
            <th className={th}>{t('wa.col_last_out', { defaultValue: 'Last out' })}</th>
            <th className={th}>{t('wa.col_gross', { defaultValue: 'Gross' })}</th>
            <th className={th}>{t('wa.col_break', { defaultValue: 'Break' })}</th>
            <th className={th}>{t('wa.col_worked', { defaultValue: 'Worked' })}</th>
            <th className={th}>{t('wa.col_source', { defaultValue: 'Source' })}</th>
            <th className={th}>{t('wa.col_status', { defaultValue: 'Status' })}</th>
            <th className={th}>{t('wa.col_flags', { defaultValue: 'Flags' })}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((d) => {
            const ev = (d.evidence || {}) as Record<string, any>;
            const cells = attendanceCells(ev);
            const chip = sourceChipOf(ev);
            const st = dayStatusOf(ev);
            const exc = exceptionCountOf(ev);
            const m = metaOf(d.employeeId);
            const basis = basisOf(ev);
            return (
              <tr key={`${d.employeeId}|${d.date}`} className="border-b border-border hover:bg-muted cursor-pointer"
                onClick={() => onOpen(d)} title={t('wa.open_detail', { defaultValue: 'Open the attendance detail' })}>
                {showDate && <td className="py-2 px-3 tabular-nums text-foreground whitespace-nowrap">{d.date}</td>}
                <td className="py-2 px-3 text-foreground whitespace-nowrap">{nameOf(d.employeeId)}</td>
                <td className="py-2 px-3 text-muted-foreground whitespace-nowrap">
                  {[m.department, m.branch].filter(Boolean).join(' · ') || '—'}
                </td>
                <td className="py-2 px-3" title={basis.detail || basis.label}><KindBadge kind={d.kind} label={d.kindLabel} /></td>
                <td className="py-2 px-3 tabular-nums text-foreground">{cells.firstIn}</td>
                <td className="py-2 px-3 tabular-nums text-foreground"
                  title={cells.lastSeen ? t('wa.last_seen', { defaultValue: 'Last seen {{time}} — the day is not over', time: cells.lastSeen }) : undefined}>
                  {cells.lastOut}
                </td>
                <td className="py-2 px-3 tabular-nums text-foreground">{cells.gross}</td>
                <td className="py-2 px-3 tabular-nums text-foreground">{cells.brk}</td>
                <td className="py-2 px-3 whitespace-nowrap"><WorkedText minutes={d.minutes} evidence={ev} /></td>
                <td className="py-2 px-3">
                  {chip ? (
                    <Badge variant="secondary" className={chip.tone}>{t(`wa.chip_${ev.sourceChip}`, { defaultValue: chip.label })}</Badge>
                  ) : <span className="text-muted-foreground">—</span>}
                </td>
                <td className={`py-2 px-3 whitespace-nowrap ${st?.tone || 'text-muted-foreground'}`} title={st?.hint}>
                  {st ? t(`wa.status_${ev.dayStatus}`, { defaultValue: st.label }) : '—'}
                </td>
                <td className="py-2 px-3">
                  {exc > 0 || d.conflict ? (
                    <span className="inline-flex items-center gap-1 text-warning" title={d.conflict ? d.conflict.replace(/_/g, ' ') : undefined}>
                      <AlertTriangle className="w-3.5 h-3.5" aria-hidden />{exc > 0 ? exc : ''}
                    </span>
                  ) : <span className="text-muted-foreground">—</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const Row: React.FC<{ k: string; v: React.ReactNode }> = ({ k, v }) => (
  <div className="flex justify-between gap-3 py-1 text-sm">
    <span className="text-muted-foreground">{k}</span>
    <span className="text-foreground text-right tabular-nums">{v}</span>
  </div>
);

export const AttendanceDayDrawer: React.FC<{
  day: PresenceDay | null;
  nameOf: (id?: string | null) => string;
  onClose: () => void;
}> = ({ day, nameOf, onClose }) => {
  const { t } = useTranslation('admin');
  const [detail, setDetail] = useState<AttendanceDayDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!day) return;
    let alive = true;
    setDetail(null); setError(null); setBusy(true);
    attendanceDayDetail(day.employeeId, day.date)
      .then((d) => { if (alive) setDetail(d); })
      .catch((e) => {
        if (!alive) return;
        // 404 = no attendance recorded that day (leave, field, nothing):
        // say so plainly rather than as an error.
        const status = (e as any)?.response?.status;
        setError(status === 404
          ? t('wa.detail_none', { defaultValue: 'No attendance was recorded for this day. The presence row comes from leave, a field assignment, or nothing at all.' })
          : getApiErrorMessage(e, t('wa.detail_err', { defaultValue: 'Could not load the attendance detail.' })));
      })
      .finally(() => { if (alive) setBusy(false); });
    return () => { alive = false; };
  }, [day, t]);

  const b = detail?.breakdown;
  return (
    <Sheet open={!!day} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" className="flex w-full flex-col overflow-y-auto sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>{day ? `${nameOf(day.employeeId)} · ${day.date}` : ''}</SheetTitle>
          <SheetDescription>
            {detail ? t('wa.detail_tz', { defaultValue: 'Times in {{tz}}', tz: detail.timezone }) : ''}
          </SheetDescription>
        </SheetHeader>
        {busy && <Loader2 className="mt-6 h-5 w-5 animate-spin text-muted-foreground" />}
        {error && <p className="mt-4 text-sm text-muted-foreground">{error}</p>}
        {detail && b && (
          <div className="mt-4 space-y-5">
            <section>
              <h4 className="text-sm font-semibold text-charcoal mb-1">{t('wa.detail_breakdown', { defaultValue: 'Breakdown' })}</h4>
              <Row k={t('wa.col_first_in', { defaultValue: 'First in' })} v={b.first_in || '—'} />
              <Row k={t('wa.col_last_out', { defaultValue: 'Last out' })} v={b.last_out || '—'} />
              <Row k={t('wa.col_gross', { defaultValue: 'Gross' })} v={fmtHM(b.gross_minutes)} />
              <Row k={t('wa.col_break', { defaultValue: 'Break' })} v={<span title={b.break_rule}>{fmtHM(b.break_minutes)}</span>} />
              <p className="text-xs text-muted-foreground">{b.break_rule}</p>
              {b.time_away_minutes > 0 && (
                <Row k={t('wa.detail_time_away', { defaultValue: 'Time away between scans (evidence, not deducted)' })} v={fmtHM(b.time_away_minutes)} />
              )}
              <Row k={t('wa.detail_net', { defaultValue: 'Worked (net)' })} v={fmtHM(b.net_minutes)} />
              <Row k={t('wa.detail_regular', { defaultValue: 'Regular' })} v={fmtHM(b.regular_minutes)} />
              <Row k={t('wa.detail_potential_ot', { defaultValue: 'Potential overtime' })} v={fmtHM(b.potential_ot_minutes)} />
              <Row k={t('wa.detail_approved_ot', { defaultValue: 'Approved overtime' })} v={fmtHM(b.approved_ot_minutes)} />
              <Row k={t('wa.detail_late', { defaultValue: 'Late by' })} v={b.late_minutes ? `${b.late_minutes} min` : '—'} />
              <Row k={t('wa.detail_early', { defaultValue: 'Left early by' })} v={b.early_departure_minutes ? `${b.early_departure_minutes} min` : '—'} />
              {detail.era.late_and_ot_withheld && (
                <p className="text-xs text-muted-foreground">
                  {t('wa.worked_late_ot_withheld', { defaultValue: 'Before go-live: lateness and overtime were not applied to this day.' })}
                </p>
              )}
            </section>

            <section>
              <h4 className="text-sm font-semibold text-charcoal mb-1">{t('wa.detail_punches', { defaultValue: 'Punches' })}</h4>
              {detail.punches.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t('wa.detail_no_punches', { defaultValue: 'No punches — this day was recorded another way.' })}</p>
              ) : (
                <ul className="space-y-1 text-sm">
                  {detail.punches.map((p) => (
                    <li key={p.at} className="flex flex-wrap items-center gap-x-2">
                      <span className="tabular-nums text-foreground">{p.time}</span>
                      <span className="text-muted-foreground">{p.device}</span>
                      {p.direction && <span className="text-muted-foreground">· {p.direction}</span>}
                      {p.network && <Badge variant="secondary" className="bg-muted text-foreground">{t(`wa.network_${p.network}`, { defaultValue: p.network })}</Badge>}
                      {p.late_arrival && <Badge variant="secondary" className="bg-warning-soft text-warning-strong">{t('wa.detail_late_marker', { defaultValue: 'late' })}</Badge>}
                      {p.received_late && (
                        <span className="text-xs text-muted-foreground">{t('wa.detail_received', { defaultValue: 'synced {{at}}', at: p.received_at })}</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section>
              <h4 className="text-sm font-semibold text-charcoal mb-1">{t('wa.detail_policy', { defaultValue: 'Policy applied' })}</h4>
              <Row k={t('wa.detail_hours', { defaultValue: 'Working hours' })} v={`${detail.policy.work_start || '—'}–${detail.policy.work_end || '—'} (+${detail.policy.grace_minutes} min grace)`} />
              <Row k={t('wa.detail_lunch', { defaultValue: 'Lunch' })} v={detail.policy.lunch_paid ? t('wa.detail_lunch_paid', { defaultValue: 'paid' }) : `${detail.policy.lunch_minutes} min after ${detail.policy.lunch_after_minutes} min`} />
              <Row k={t('wa.detail_ot_cap', { defaultValue: 'Daily overtime cap' })} v={`${detail.policy.overtime_daily_cap_minutes} min`} />
              <Row k={t('wa.detail_measured_from', { defaultValue: 'Measured from' })} v={detail.era.measured_from || '—'} />
            </section>

            {(detail.exceptions.length > 0 || detail.correction || detail.approval.overtime) && (
              <section>
                <h4 className="text-sm font-semibold text-charcoal mb-1">{t('wa.detail_review', { defaultValue: 'Review' })}</h4>
                <Row k={t('wa.detail_day_status', { defaultValue: 'Day' })} v={detail.approval.day_status || '—'} />
                {detail.approval.overtime && (
                  <Row k={t('wa.detail_overtime', { defaultValue: 'Overtime request' })}
                    v={`${detail.approval.overtime.status}${detail.approval.overtime.sent_to_payroll_at ? ` · ${t('wa.detail_sent_payroll', { defaultValue: 'sent to payroll' })}` : ''}`} />
                )}
                {detail.correction && (
                  <p className="text-sm text-foreground">{t('wa.detail_corrected', { defaultValue: 'Corrected by hand: {{reason}}', reason: detail.correction.reason || '—' })}</p>
                )}
                <ul className="mt-1 space-y-1">
                  {detail.exceptions.map((e) => (
                    <li key={e.id} className="text-sm">
                      <span className={e.status === 'open' ? 'text-warning' : 'text-muted-foreground'}>{e.type.replace(/_/g, ' ')}</span>
                      <span className="text-muted-foreground"> · {e.status}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
